import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate, Outlet } from "react-router-dom";
import { Toaster } from "sonner";
import api from "@/lib/api";
import FloatingContact from "@/components/FloatingContact";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import { CookieConsent } from "@/components/CookieConsent";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AcceptTeam from "@/pages/AcceptTeam";
import AuthCallback from "@/pages/AuthCallback";
import Onboarding from "@/pages/Onboarding";
import Subscribe from "@/pages/Subscribe";
import CompleteRegistration from "@/pages/CompleteRegistration";
import PaymentSuccess from "@/pages/PaymentSuccess";
import Legal from "@/pages/Legal";
import Dashboard from "@/pages/Dashboard";
import Processes from "@/pages/Processes";
import ProcessDetail from "@/pages/ProcessDetail";
import Deadlines from "@/pages/Deadlines";
import Assistant from "@/pages/Assistant";
import Legislation from "@/pages/Legislation";
import LegislationDetail from "@/pages/LegislationDetail";
import Checklists from "@/pages/Checklists";
import Documents from "@/pages/Documents";
import SubscriptionPage from "@/pages/SubscriptionPage";
import Settings from "@/pages/Settings";
import Support from "@/pages/Support";
import Notifications from "@/pages/Notifications";
import Admin from "@/pages/Admin";
import Intelligence from "@/pages/Intelligence";
import Diagnostico from "@/pages/Diagnostico";
import GeoIntel from "@/pages/GeoIntel";

function Spinner() {
    return (
        <div className="min-h-screen flex items-center justify-center bg-background" data-testid="loading-spinner">
            <div className="w-10 h-10 border-4 border-petroleum/20 border-t-petroleum rounded-full animate-spin" />
        </div>
    );
}

function RequireAuth() {
    const { user, loading } = useAuth();
    if (loading) return <Spinner />;
    if (!user) return <Navigate to="/login" replace />;
    return <Outlet />;
}

function AppLayout() {
    const { user, loading } = useAuth();
    if (loading) return <Spinner />;
    if (!user) return <Navigate to="/login" replace />;
    if (!user.onboarded) return <Navigate to="/onboarding" replace />;
    if (!user.sub_active && !user.is_admin) return <Navigate to="/assinatura" replace />;
    return <AppShell><Outlet /></AppShell>;
}

function AdminLayout() {
    const { user, loading } = useAuth();
    if (loading) return <Spinner />;
    if (!user) return <Navigate to="/login" replace />;
    if (!user.onboarded) return <Navigate to="/onboarding" replace />;
    if (!user.is_admin) return <Navigate to="/dashboard" replace />;
    return <AppShell><Outlet /></AppShell>;
}

function VisitTracker() {
    const location = useLocation();
    React.useEffect(() => {
        let vid = localStorage.getItem("gm_visitor_id");
        if (!vid) {
            vid = (crypto.randomUUID ? crypto.randomUUID() : String(Date.now()) + Math.random());
            localStorage.setItem("gm_visitor_id", vid);
        }
        api.post("/track/visit", { visitor_id: vid, path: location.pathname, referrer: document.referrer || "" }).catch(() => {});
    }, [location.pathname]);
    return null;
}

function AppRouter() {
    const location = useLocation();
    if (location.hash?.includes("session_id=")) {
        return <AuthCallback />;
    }
    return (
        <>
        <VisitTracker />
        <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/esqueci-senha" element={<ForgotPassword />} />
            <Route path="/redefinir-senha" element={<ResetPassword />} />
            <Route path="/entrar-equipe" element={<AcceptTeam />} />
            <Route path="/assinatura" element={<Subscribe />} />
            <Route path="/concluir-cadastro" element={<CompleteRegistration />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/privacidade" element={<Legal page="privacidade" />} />
            <Route path="/termos" element={<Legal page="termos" />} />
            <Route path="/cookies" element={<Legal page="cookies" />} />

            <Route element={<RequireAuth />}>
                <Route path="/onboarding" element={<Onboarding />} />
            </Route>

            <Route element={<AppLayout />}>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/processos" element={<Processes />} />
                <Route path="/inteligencia" element={<Intelligence />} />
                <Route path="/diagnostico-anm" element={<Diagnostico />} />
                <Route path="/inteligencia-geoespacial" element={<GeoIntel />} />
                <Route path="/processos/:id" element={<ProcessDetail />} />
                <Route path="/prazos" element={<Deadlines />} />
                <Route path="/assistente" element={<Assistant />} />
                <Route path="/legislacao" element={<Legislation />} />
                <Route path="/legislacao/:id" element={<LegislationDetail />} />
                <Route path="/checklists" element={<Checklists />} />
                <Route path="/documentos" element={<Documents />} />
                <Route path="/plano" element={<SubscriptionPage />} />
                <Route path="/configuracoes" element={<Settings />} />
                <Route path="/suporte" element={<Support />} />
                <Route path="/notificacoes" element={<Notifications />} />
            </Route>

            <Route element={<AdminLayout />}>
                <Route path="/admin" element={<Admin />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <FloatingContact />
        <CookieConsent />
        </>
    );
}

export default function App() {
    return (
        <div className="App">
            <AuthProvider>
                <BrowserRouter>
                    <Toaster position="top-right" richColors />
                    <AppRouter />
                </BrowserRouter>
            </AuthProvider>
        </div>
    );
}
